import { usePathname } from "next/navigation";
import React from "react";

import { CountryCode } from "@/types";
import { deleteHCPConfirmedCookie } from "@/utils/getCookieHelpers";

import { useExperience } from "./useExperience";

interface CountrySelectorContextValue {
  countrySelected: boolean;
  countrySelection: CountryCode | null;
  countrySelectionSet: (countryCode: CountryCode | null) => void;
  countrySelectorVisible: boolean;
}

export const CountrySelectorContext = React.createContext<CountrySelectorContextValue>({
  countrySelected: false,
  countrySelection: null,
  countrySelectionSet: () => {
    throw new Error("A CountrySelectorProvider is required for the useCountrySelector hook");
  },
  countrySelectorVisible: false
});

export const CountrySelectorProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const pathname = usePathname();
  const { pathCountry, pathCountrySupported, confirmedHCP } = useExperience();
  const [countrySelection, countrySelectionSet] = React.useState<CountryCode | null>(null);
  const countrySelected = !!pathCountry;
  const countrySelectorVisible = !pathCountry || !confirmedHCP || !pathCountrySupported;

  React.useEffect(() => {
    // Remove the HCP confirmation if we are on the home page and a
    // country isn't selected
    if (pathname === "/~/temperature-excursion" && !pathCountry) {
      deleteHCPConfirmedCookie();
    }
  }, [pathname, pathCountry]);

  return (
    <CountrySelectorContext.Provider
      value={{
        countrySelected,
        countrySelection,
        countrySelectionSet,
        countrySelectorVisible
      }}
    >
      {children}
    </CountrySelectorContext.Provider>
  );
};

export const useCountrySelector = () => React.useContext(CountrySelectorContext);
